import React, { useState } from 'react';
import { usePracticeMetrics } from './hooks/usePracticeMetrics';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { exportPracticeMetrics } from '../../../../utils/exportToExcel';
import { IconDownload } from '@tabler/icons-react';

const TIER_COLORS = {
  'power': '#10b981',
  'active': '#3b82f6',
  'light': '#f59e0b',
  'inactive': '#ef4444'
};

const TIER_LABELS = {
  'power': 'Power (51+ checks)',
  'active': 'Active (11-50 checks)',
  'light': 'Light (1-10 checks)',
  'inactive': 'Inactive (0 checks)'
};

const PracticePerformance = () => {
  const [dateRange, setDateRange] = useState('30d');
  const { data, loading, error, refetch } = usePracticeMetrics({ dateRange });

  // Calculate metrics
  const totalPractices = data.activePractices?.practices?.length || 0;
  const avgChecks = totalPractices > 0
    ? (data.activePractices.practices.reduce((sum, p) => sum + p.total_checks, 0) / totalPractices).toFixed(1)
    : '--';
  const avgUsers = totalPractices > 0
    ? (data.activePractices.practices.reduce((sum, p) => sum + p.active_users, 0) / totalPractices).toFixed(1)
    : '--';

  // Calculate overall retention rate from tier data
  const overallRetention = data.practicesByUsageTier?.tier_data?.length > 0
    ? data.practicesByUsageTier.tier_data
        .filter(t => t.usage_tier !== 'inactive')
        .reduce((sum, t) => sum + t.percentage, 0)
        .toFixed(1)
    : '--';

  // Helper function for safe date formatting
  const formatDate = (dateStr) => {
    try {
      if (dateStr) {
        const parsedDate = new Date(dateStr);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
        }
      }
    } catch (e) {
      console.error('Error parsing date:', dateStr, e);
    }
    return 'Invalid Date';
  };

  // Prepare chart data
  const tierPieData = data.practicesByUsageTier?.tier_data
    ? data.practicesByUsageTier.tier_data.map(item => ({
        name: TIER_LABELS[item.usage_tier] || item.usage_tier,
        value: item.practice_count,
        percentage: item.percentage,
        tier: item.usage_tier
      }))
    : [];

  const engagementTrendData = data.practiceEngagementTrends?.trends_data
    ? [...data.practiceEngagementTrends.trends_data].reverse().map(item => ({
        week: formatDate(item.week_start),
        'Active Practices': item.active_practices,
        'New Practices': item.new_practices,
        'Avg Checks': item.avg_checks_per_practice
      }))
    : [];

  return (
    <div className="p-6 space-y-6">
      <div className="border-b pb-4">
        <h1 className="text-3xl font-bold text-gray-900">Practice Performance</h1>
        <p className="text-gray-600 mt-2">
          Monitor practice engagement, retention, and usage patterns
        </p>
      </div>

      {/* Date Range Filter */}
      <div className="flex justify-end gap-3">
        <button
          onClick={() => exportPracticeMetrics(data, dateRange)}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-[#2563eb] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <IconDownload size={18} />
          Export to Excel
        </button>
        <select 
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="border border-gray-300 rounded-md px-4 py-2 bg-white text-sm"
        >
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
        </select>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center justify-between">
          <span>Error loading data: {error.message}</span>
          <button
            onClick={refetch}
            className="ml-4 px-3 py-1 bg-red-100 hover:bg-red-200 text-red-800 rounded text-sm font-medium transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Metric Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-sm font-medium text-gray-600">Active Practices</h3>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {loading ? '...' : totalPractices}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {loading ? 'Loading...' : `In ${dateRange}`}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-sm font-medium text-gray-600">Avg Checks/Practice</h3>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {loading ? '...' : avgChecks}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {loading ? 'Loading...' : 'Per practice'}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-sm font-medium text-gray-600">Avg Users/Practice</h3>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {loading ? '...' : avgUsers}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {loading ? 'Loading...' : 'Active users'}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-sm font-medium text-gray-600">Retention Rate</h3>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {loading ? '...' : `${overallRetention}%`}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {loading ? 'Loading...' : 'Active practices'}
          </p>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Practices by Usage Tier</h3>
          {loading ? (
            <div className="h-64 flex items-center justify-center text-gray-400">
              Loading tier distribution...
            </div>
          ) : tierPieData.length > 0 ? (
            <div className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={tierPieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name.split(' ')[0]}: ${percentage.toFixed(1)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {tierPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={TIER_COLORS[entry.tier] || '#6b7280'} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 text-sm text-gray-600">
                Total Practices: {data.practicesByUsageTier?.total_practices || 0}
              </div>
            </div>
          ) : (
            <div className="h-64 flex flex-col items-center justify-center text-gray-400">
              <p>No tier data available</p>
              <p className="text-sm mt-2">Data will appear as practices become active</p>
            </div>
          )}
        </div>
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Practice Engagement Trends</h3>
          {loading ? (
            <div className="h-64 flex items-center justify-center text-gray-400">
              Loading engagement trends...
            </div>
          ) : engagementTrendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={engagementTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="Active Practices" stroke="#3b82f6" strokeWidth={2} />
                <Line type="monotone" dataKey="New Practices" stroke="#10b981" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex flex-col items-center justify-center text-gray-400">
              <p>No trend data available</p>
              <p className="text-sm mt-2">Data will accumulate over time</p>
            </div>
          )}
        </div>
      </div>

      {/* Top Practices Table */}
      <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Top Practices</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Practice Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Active Users
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prescribers
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Staff
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Checks
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-400">
                    Loading practice data...
                  </td>
                </tr>
              ) : data.activePractices?.practices?.length > 0 ? (
                [...data.activePractices.practices]
                  .sort((a, b) => b.total_checks - a.total_checks)
                  .slice(0, 10)
                  .map((practice, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {practice.practice_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {practice.active_users}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {practice.active_prescribers || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {practice.active_staff || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {practice.total_checks}
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-400">
                    No practice data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Practice Retention Table */}
      <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Practice Weekly Retention</h3>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="h-48 flex items-center justify-center text-gray-400">
              Loading retention data...
            </div>
          ) : data.practiceLevelRetention?.retention_data?.length > 0 ? (
            <table className="min-w-full text-sm divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Practice</th>
                  <th className="px-4 py-2 text-center font-medium text-gray-600">Week 0</th>
                  <th className="px-4 py-2 text-center font-medium text-gray-600">Week 1</th>
                  <th className="px-4 py-2 text-center font-medium text-gray-600">Week 2</th>
                  <th className="px-4 py-2 text-center font-medium text-gray-600">Week 3</th>
                  <th className="px-4 py-2 text-center font-medium text-gray-600">Week 4</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.practiceLevelRetention.retention_data.slice(0, 10).map((practice, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium text-gray-900">
                      {practice.practice_name}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {practice.week_0_active ? <span className="text-green-600">✓</span> : <span className="text-red-400">✗</span>}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {practice.week_1_active ? <span className="text-green-600">✓</span> : <span className="text-red-400">✗</span>}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {practice.week_2_active ? <span className="text-green-600">✓</span> : <span className="text-red-400">✗</span>}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {practice.week_3_active ? <span className="text-green-600">✓</span> : <span className="text-red-400">✗</span>}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {practice.week_4_active ? <span className="text-green-600">✓</span> : <span className="text-red-400">✗</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="h-48 flex flex-col items-center justify-center text-gray-400">
              <p>No retention data available</p>
              <p className="text-sm mt-2">Data requires at least 5 weeks of activity</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PracticePerformance;
